import { BlockError } from '@/blocks.04-project/block-error/block-error';
import forEach from 'lodash-es/forEach';
import get from 'lodash-es/get';
import debounce from 'lodash-es/debounce';
import find from 'lodash-es/find';
import compact from 'lodash-es/compact';
import map from 'lodash-es/map';
import toArray from 'lodash-es/toArray';
import isNull from 'lodash-es/isNull';
import counter from '@/blocks.04-project/counter/counter.tmpl-specs/base.module';
import toNumber from 'lodash-es/toNumber';

/**
 * Класс для работы с подарочными наборами
 * @example
 *
 * Аттрибуты:
 * [data-gift-set-id] - {string} - id набора
 *
 * Аттрибуты на элементах:
 * [data-gift-basket] - {boolean} - элемент работы с корзиной
 * [data-control-counter] - {boolean} - элемент каунтера
 * [data-control-cart] - {boolean} - элемент добавления в корзину
 */
class GiftSetControl {
  /***
   * Конструктор класса
	 * @param blockClass {String} класс блока, от которого наследуется
	 * @param selector {String|undefined} селектор для поиска блока
	 * @param options {Object} данные набора
	 * @param elem {HTMLElement|undefined} элемент для инициализации
   */
  constructor({ blockClass, selector, options, elem }) {
		this.BlockError = new BlockError({ blockName: 'GiftSetControl', destroy: this.destroy });

		this.blockClass = blockClass;
		this.controlSelector = '.product-control, .gift-set-control';

		this.selector = selector;
		this.block = elem || document.querySelector(this.selector);
		if (!this.block) return this.BlockError.error(`Отсутствует ${this.selector} или ${elem}`);

		this.block.GiftSetControl = this;
		this.getData(options);

		window.getBH((bh) => { this.bh = bh; });
  }

  init() {
		if (this.block.classList.contains('gift-set-control_inited')) return this.BlockError.error(`Повторная инициализация ${this.block}`);

		this.block.setAttribute('data-gift-set-id', this.id);
		this.render();

		this.block.addEventListener('change', this.changeHandler);
		this.block.addEventListener('click', this.clickHandler);

    this.block.classList.add('gift-set-control_inited');
  }

	render() {
		this.renderControl();
	}

	/***
	 * Обработка всех событий change на блоке
	 * @param event - событие
	 */
	changeHandler = (event) => {
		this.change(event);
	};

	/***
	 * Обработка всех событий change на блоке
	 * @param event - событие
	 */
	change(event) {
		const closestControl = event.target.closest(this.controlSelector);
		if (closestControl !== this.block) return;

		if (event.target.closest('[data-control-counter]') && event.target.tagName === 'INPUT') {
			const oldQuantity = toNumber(get(find(window.Block.Basket.giftset, { id: this.id }), 'quantity', 0));

			event.preventDefault();
			const quantity = toNumber(event.target.value);
			quantity < 1 && this.toggleCounter({ toggle: false });
			forEach(this.getGiftSets(), (card) => card?.GiftSetControl?.toggleCounter({
				toggle: quantity >= 1, quantity: quantity,
			}));
			this.updateGiftset(quantity, oldQuantity);
		}
	}

	/***
	 * Обработка всех событий change на блоке
	 * @param event - событие
	 */
	clickHandler = (event) => {
		this.click(event);
	};

	/***
	 * Обработка всех событий change на блоке
	 * @param event - событие
	 */
	click(event) {
		const closestControl = event.target.closest(this.controlSelector);
		if (closestControl !== this.block) return;

		if (event.target.closest('[data-control-cart]')) {
			const oldQuantity = toNumber(get(find(window.Block.Basket.giftset, { id: this.id }), 'quantity', 0));
			if (oldQuantity !== 0) return;

			event.preventDefault();
			this.toggleCounter({ toggle: true });
			forEach(this.getGiftSets(), (card) => card?.GiftSetControl?.toggleCounter({
				toggle: true, quantity: 1,
			}));
			this.updateGiftset(1, oldQuantity);
		}
	}

	renderControl() {
		this.renderBasket();
	}

	renderBasket() {
		const quantity = toNumber(get(find(window.Block.Basket.giftset, { id: this.id }), 'quantity', 0));
		const isInBasket = quantity > 0;
		const isExistBasket = !isNull(get(window, 'Block.Basket.giftset', null));

		const containers = this.getBlockElements('[data-gift-basket]');
		if (!containers.length) return;

		forEach(containers, (container) => {
			container.innerHTML = '';
			container.insertAdjacentHTML('beforeend', this.bh.apply([
				{block: this.blockClass, elem: 'counter', content: [
						counter({
							attrs: {
								'data-control-counter': true,
							},
							value: quantity,
							max: this.max,
							disabled: !isExistBasket
						}),
					]},
				{block: 'btn', cls: 'btn-dark btn-lg', tag: 'button',
					attrs: {
						'data-control-cart': true,
						disabled: !isExistBasket
					},
					content: window.BX?.message('addInBasket') || 'В корзину'},
			]));
		});

		this.toggleCounter({ toggle: isExistBasket && isInBasket });
	}

	/***
	 * Переключение каунтера на кнопку и обратно. Обновление значения каунтера
	 * @param toggle {boolean} - добавлен/удалён
	 * @param quantity {Number} - количество в каунтере
	 */
	toggleCounter({ toggle, quantity = 1 }) {
		const baskets = this.getBlockElements('[data-gift-basket]');
		forEach(baskets, async (basket) => {
			basket.classList.toggle('gift-set-control_in-basket', toggle);

			const counter = basket.querySelector('.counter');
			if (!counter) return;

			if (!counter?.Counter) {
				if (!toggle) return;
				window.Block.Counter.init(basket, true);
			}
			const ins = await counter?.AsyncCounter;
			ins.input.value = toggle ? quantity : 0;
			ins.changeHandler();
		});
	}

	/***
	 * Переключение работы к каунтера и кнопки в корзину
	 * @param toggle {Boolean} - вкл/выкл
	 * @param limit {Boolean} - если достигнут лимит
	 */
	toggleDisabledCounter({ toggle, limit }) {
		const cartButtons = this.getBlockElements('[data-control-cart]');
		const cartCounters = this.getBlockElements('.counter');

		forEach(cartCounters, async (counter) => {
			counter.classList.toggle('disabled', toggle);
			const ins = await counter?.AsyncCounter;
			if (toggle || limit) ins?.plus.setAttribute('disabled', '');
			else ins?.plus.removeAttribute('disabled');
		});

		forEach(cartButtons, (btn) => {
			if (toggle) btn.setAttribute('disabled', '');
			else btn.removeAttribute('disabled');
		});
	}

	/***
	 * Получение данных из переданных параметров
	 * @param options{Object} - параметры
	 */
	getData(options) {
		this.data = get(options, 'data', {});
		this.id = get(this.data, 'id', null);
		this.max = get(this.data, 'max', 0);
	}

	/***
	 * Получение элементов текущего блока
	 * @param selector{String} - селектор поиска
	 * @return {HTMLElement[]} - массив элементов
	 */
	getBlockElements(selector) {
		return compact(map(toArray(this.block.querySelectorAll(selector)), (el) => {
			if (el.closest(this.controlSelector) === this.block) return el;
		}));
	}

	/***
	 * Получение всех связанных гифтсетов
	 * @return {HTMLElement[]} - массив карточек
	 */
	getGiftSets() {
		return map(toArray(document.querySelectorAll(`.gift-set-control[data-gift-set-id="${this.id}"]`)), (el) => {
			if (el !== this.block) return el;
		});
	}

	/***
	 * Обновление гифтсета в корзине
	 * @param quantity {Number} - новое количество
	 * @param oldQuantity {Number} - старое количество
	 */
	updateGiftset = debounce((quantity, oldQuantity) => {
		this.toggleDisabledCounter({ toggle: true });
		forEach(this.getGiftSets(), (card) => card?.GiftSetControl?.toggleDisabledCounter({ toggle: true }));
		window.Block.Basket.updateGiftset({ id: this.id, quantity })
			.then((res) => {
				if (res) {
					const limit =  get(res, 'limit', false);
					const newQuantity = toNumber(get(res, 'quantity', 0));
					const toggle = limit || newQuantity === 0 && toNumber(quantity) !== 0;

					this.toggleCounter({ toggle: !toggle && quantity > 0, quantity: newQuantity, });
					this.toggleDisabledCounter({ toggle, limit: newQuantity >= this.max });

					forEach(this.getGiftSets(), (card) => {
						card?.GiftSetControl?.toggleCounter({ toggle: !toggle && quantity > 0, quantity: newQuantity, });
						card?.GiftSetControl?.toggleDisabledCounter({ toggle, limit: newQuantity >= this.max });
					});

					// Добавлено по просьбе программистов, им нужно отслеживать когда поменялось корзина
					this.block.dispatchEvent(new CustomEvent('updateBasket.iv.productControl', { bubbles: true, detail: {
							quantity,
							oldQuantity,
							currentOffer: this.id,
						}}));
				}
			});
	}, 400);

  destroy() {
    if (this.block) {
			this.block.removeEventListener('change', this.changeHandler);
			this.block.removeEventListener('click', this.clickHandler);
      this.block.classList.remove('gift-set-control_inited');
      delete this.block.GiftSetControl;
    }
  }

	static updateControl() {
		forEach(document.querySelectorAll('.gift-set-control_inited'), (elem) => {
			const giftSet = get(elem, 'GiftSetControl', null);
			giftSet && giftSet.renderControl();
		});
	}
}

// Экспорт;
export default GiftSetControl;
window.Block.GiftSetControl = GiftSetControl;
